import { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	ArrowLeftIcon,
	CheckIcon,
	Edit03Icon,
	MessageTextSquare01Icon
} from '@/components/ui/icons'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import { MultipleFlagsPopoverTrigger } from '@/features/photoset/components/flags-popover/multiple-flags-popover-trigger'
import { GalleryRetouchingQaModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-retouching-qa-modal/gallery-retouching-qa-modal'
import { ImagesCounter } from '@/features/photoset/components/images-counter'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { RetouchingQaCommentConfirmModal } from '@/features/photoset/components/retouching-qa-comment-confirm-modal'
import { RetouchingQaCommentModal } from '@/features/photoset/components/retouching-qa-comment-modal'
import { RetouchingQaConfirmationModal } from '@/features/photoset/components/retouching-qa-confirmation-modal/retouching-qa-confirmation-modal'
import { RetouchingQaSpaceImages } from '@/features/photoset/components/retouching-qa-space-images'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useCurrentPhotosetSubRoute } from '@/features/photoset/hooks/use-current-photoset-sub-route'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import { GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { useUpdateStepToVettingQa } from '@/features/photoset/queries/use-update-step-to-vetting-qa'
import { usePhotosetStore } from '@/features/photoset/store/store'
import {
	type PhotosetSpace,
	type PhotosetURLParams,
	StepKey
} from '@/features/photoset/types'
import {
	retrievePhotosetSpacesFromSelectedImages,
	spaceNameToLabel
} from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

interface SelectedComment {
	photosetSpaceId: PhotosetSpace['id']
	spaceName: string
	comment?: string
}

export const PhotosetRetouchingQaStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [selectedComment, setSelectedComment] =
		useState<SelectedComment | null>(null)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const images = usePhotosetStore((state) => state.images)
	const currentPhotosetSubRoute = useCurrentPhotosetSubRoute()

	//statuses
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	// queries
	const {
		data: photosetSpaces,
		isFetching: isFetchingPhotosetSpaces,
		isError: isErrorPhotosetSpaces
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: {
			response_type: GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE,
			is_final_select: true
		}
	})

	// mutations
	const {
		mutate: updateStepToVettingQa,
		isPending: isPendingUpdateStepToVettingQa
	} = useUpdateStepToVettingQa({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)
	const flagsQuery = useGetFlags(
		{
			params: {
				types: ['visual_retouching'],
				response_type: 'extended'
			}
		},
		{ staleTime: Number.POSITIVE_INFINITY }
	)

	// values
	const allFlagsKeyName = useMemo(
		() => flagsQuery.data?.map((flag) => flag.key_name),
		[flagsQuery.data]
	)

	const hasFlags = useMemo(() => {
		const extractFlagKeysFromImages = (photosetSpace: PhotosetSpace) => {
			return photosetSpace.images.flatMap((image) =>
				image.flags.map((flag) => flag.key_name)
			)
		}

		const allFlagKeys = photosetSpaces?.flatMap(extractFlagKeysFromImages)

		return allFlagKeys?.some((flagKeyName) =>
			allFlagsKeyName?.includes(flagKeyName)
		)
	}, [photosetSpaces, allFlagsKeyName])

	const isQaView = useMemo(
		() => currentPhotosetSubRoute === 'qa',
		[currentPhotosetSubRoute]
	)

	// Handle gallery modal view
	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		currentDetailSpaceIndex,
		currentDetailSpace,
		onExpandImage,
		onReduceImage
	} = useExpandPhotosetImage()

	//init Photoset Spaces
	useEffect(() => {
		if (photosetSpaces) {
			initPhotosetSpaces(photosetSpaces)
		}
	}, [photosetSpaces, initPhotosetSpaces])

	// Retrieve selected photoset spaces from selected images
	const selectedPhotosetSpaces = useMemo(() => {
		return retrievePhotosetSpacesFromSelectedImages(images, selectedImages)
	}, [images, selectedImages])

	return (
		<>
			<PageHero
				endSlot={
					selectedImages.length >= 1 ? (
						isQaView && (
							<div className='flex items-center gap-2'>
								<ImagesCounter count={selectedImages.length} />

								<FlagsPopover
									photosetSpaceIds={selectedPhotosetSpaces}
									selectedImagesIds={selectedImages}
									stepId={StepKey['retouching-qa']}
									trigger={<MultipleFlagsPopoverTrigger />}
									types={['visual_retouching']}
								/>

								<ButtonLegacy
									onClick={() => unselectAll()}
									size='sm'
									type='button'
									variant='tertiary'
								>
									{t('common:unselect_all')}
								</ButtonLegacy>
							</div>
						)
					) : (
						<StepHeroControls hideSortControls />
					)
				}
				subtitle={t('photoset:retouching_qa.subtitle')}
				title={t('photoset:retouching_qa.title')}
			/>

			{showConfirmationModal && !hasFlags && (
				<RetouchingQaConfirmationModal
					onClose={() => setShowConfirmationModal(false)}
					photosetId={urlParams.id}
				/>
			)}

			{showConfirmationModal && hasFlags && (
				<RetouchingQaCommentConfirmModal
					onClose={() => setShowConfirmationModal(false)}
					photosetId={urlParams.id}
				/>
			)}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					{isFetchingPhotosetSpaces &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(isFetchingPhotosetSpaces || isErrorPhotosetSpaces) &&
						photosetSpaces?.map(
							(photosetSpace, spaceIndex) =>
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<RetouchingQaSpaceImages
												allFlagsKeyName={
													allFlagsKeyName
												}
												images={photosetSpace.images}
												isQaView={isQaView}
												onExpandImage={onExpandImage}
												photosetId={urlParams.id}
												photosetSpaceId={
													photosetSpace.id
												}
												spaceIndex={spaceIndex}
											/>
										}
										key={photosetSpace.space.id}
										spaceHeaderEndSlot={
											photosetSpace.comment ? (
												<div className='flex items-center gap-2 [&_svg]:w-3.5'>
													{isQaView && (
														<ButtonLegacy
															onClick={() =>
																setSelectedComment(
																	{
																		photosetSpaceId:
																			photosetSpace.id,
																		spaceName:
																			spaceNameToLabel[
																				photosetSpace
																					.space
																					.key_name
																			],
																		comment:
																			photosetSpace.comment ??
																			undefined
																	}
																)
															}
															shape='square'
															size='xs'
															type='button'
															variant='secondary'
														>
															<Edit03Icon
																size={12}
															/>
														</ButtonLegacy>
													)}
													<p className='max-w-[600px] overflow-hidden text-ellipsis whitespace-nowrap'>
														{photosetSpace.comment}
													</p>
												</div>
											) : (
												isQaView && (
													<ButtonLegacy
														className='p-0 text-neutral-600'
														onClick={() =>
															setSelectedComment({
																photosetSpaceId:
																	photosetSpace.id,
																spaceName:
																	spaceNameToLabel[
																		photosetSpace
																			.space
																			.key_name
																	]
															})
														}
														size='sm'
														type='button'
														variant='tertiary'
													>
														<MessageTextSquare01Icon
															size={20}
														/>
														<span>
															{t(
																'photoset:retouching_qa.comment_button'
															)}
														</span>
													</ButtonLegacy>
												)
											)
										}
										spaceName={photosetSpace.space.key_name}
									/>
								)
						)}
				</PhotosetStepBodyContainer>
			</PhotosetStepWrapper>

			{isQaView && (
				<>
					<PageFooter
						centerSlot={
							<p>
								{t('photoset:retouching_qa.footer_step_label')}
							</p>
						}
						endSlot={
							<ButtonLegacy
								onClick={() => setShowConfirmationModal(true)}
								type='button'
								variant='primarySuccess'
							>
								<span>{t('common:done')}</span>
								<CheckIcon />
							</ButtonLegacy>
						}
						startSlot={
							<ButtonLegacy
								disabled={isPendingUpdateStepToVettingQa}
								onClick={() =>
									updateStepToVettingQa(urlParams.id)
								}
								type='button'
								variant='tertiary'
							>
								<ArrowLeftIcon />
								<span>{t('common:back')}</span>
							</ButtonLegacy>
						}
					/>
					{selectedComment && (
						<RetouchingQaCommentModal
							{...selectedComment}
							onClose={() => setSelectedComment(null)}
						/>
					)}
				</>
			)}

			{currentDetailImageIndex !== null &&
				currentDetailSpaceIndex !== null &&
				currentDetailSpace && (
					<GalleryRetouchingQaModal
						currentDetailImageIndex={currentDetailImageIndex}
						currentDetailSpaceIndex={currentDetailSpaceIndex}
						currentPhotosetSpaceId={currentDetailSpace.id}
						currentSpaceName={currentDetailSpace.space.key_name}
						isQaView={isQaView}
						onClose={onReduceImage}
						selectableImages={currentDetailSpace.images}
						setCurrentDetailImageIndex={setCurrentDetailImageIndex}
					/>
				)}
		</>
	)
}
